import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { flexRender, useReactTable } from "@tanstack/react-table";
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowData,
} from "@tanstack/table-core";
import { capitalCase } from "change-case";

import { MoneyBadge } from "@/components/MoneyBadge";
import { Spinner } from "@/components/Spinner";
import { FeedAvatar, Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNextLink,
  PaginationPreviousLink,
} from "@/components/ui/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  GetCampaignHistoryItem,
  GetCampaignHistoryOptions,
  getCampaignHistoryQueryOptions,
  getCurrentUserQueryOptions,
} from "@/lib/api";
import { CampaignState, campaignStateOptions, User } from "@/lib/types";
import { cn } from "@/lib/utils";

function CampaignStateBadge({
  campaign,
}: {
  campaign: GetCampaignHistoryItem;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge asChild
          noIcon
          variant={
            [
              "denied",
              "inactive",
              "deactivated",
              "rescinded",
              "cancelled",
            ].includes(campaign.state)
              ? "destructive"
              : ["proposed", "accepted"].includes(campaign.state)
              ? "warning"
              : "success"
          }
        >
          <button type="button">{capitalCase(campaign.state)}</button>
        </Badge>
      </PopoverTrigger>
      <PopoverContent>
        {campaign.metadata?.reason ? (
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-xs">Reason</div>
            <div>{campaign.metadata.reason}</div>
          </div>
        ) : (
          "No reason provided"
        )}
      </PopoverContent>
    </Popover>
  );
}

function CampaignMoney({
  campaign,
  user,
}: {
  campaign: GetCampaignHistoryItem;
  user: User | undefined;
}) {
  return (
    <MoneyBadge
      amount={campaign.cpm_inflight}
      variant={
        campaign.cpm_inflight === 0
          ? "neutral"
          : user?.user_id === campaign.campaign_user.did
          ? "negative"
          : "positive"
      }
    />
  );
}

const columns: ColumnDef<GetCampaignHistoryItem>[] = [
  {
    header: "State",
    cell: ({ row }) => <CampaignStateBadge campaign={row.original} />,
  },
  {
    header: "Feed",
    cell: ({ row }) => {
      return (
        <Link
          className="flex gap-3 items-center"
          to="/app/campaigns/history"
          search={{ page: 1, algo: row.original.algorithm.id }}
        >
          <FeedAvatar feed={row.original.algorithm} className="w-8 h-8" />
          <div className="truncate w-full">
            {row.original.algorithm.display_name}
          </div>
        </Link>
      );
    },
  },
  {
    header: "Campaign",
    cell: ({ row, table }) => {
      return (
        <SimpleTooltip message="View campaign">
          {table.options?.meta?.user?.id === row.original.campaign.user_id ? (
            <Link
              className="flex gap-3 items-center bg-slate-subtle rounded-lg p-2 border border-slate-normal w-fit text-xs"
              to="/app/campaigns/$campaignId/view"
              params={{ campaignId: row.original.campaign.id.toString() }}
            >
              {row.original.campaign.display_name}
            </Link>
          ) : (
            <Link
              className="flex gap-3 items-center bg-slate-subtle rounded-lg p-2 border border-slate-normal w-fit text-xs"
              to="/app/campaigns/$campaignId/review/$algoId"
              params={{
                campaignId: row.original.campaign.id.toString(),
                algoId: row.original.algorithm.id.toString(),
              }}
            >
              {row.original.campaign.display_name}
            </Link>
          )}
        </SimpleTooltip>
      );
    },
  },
  {
    header: "From User",
    cell: ({ row }) => (
      <div className="flex gap-2 items-center">
        <Avatar
          size="sm"
          src={row.original.campaign_user.avatar}
          alt={row.original.campaign_user.handle}
        />
        <div className="flex flex-col gap-0.5">
          <div>{row.original.campaign_user.display_name}</div>
          <a
            className="text-slate-dim text-xs"
            href={`https://bsky.app/profile/${row.original.campaign_user.handle}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            @{row.original.campaign_user.handle}
          </a>
        </div>
      </div>
    ),
  },
  {
    header: "Impressions",
    cell: ({ row }) => (
      <span>
        <span>{row.original.impressions_to_date}</span>
        <span className="text-slate-dim">
          {" / "}
          {row.original.impressions_budget + row.original.impressions_to_date}
        </span>
      </span>
    ),
  },
  {
    header: "Money",
    cell: ({ row, table }) => (
      <CampaignMoney campaign={row.original} user={table.options.meta?.user} />
    ),
  },
];

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    user: User | undefined;
  }
}

export const Route = createFileRoute(
  "/app/_sidebar-layout/_campaigns-layout/campaigns/history",
)({
  component: CampaignTables,
  validateSearch: (search: Record<string, unknown>) => {
    // validate and parse the search params into a typed state
    return {
      state: search?.state,
      algo: search?.algo,
      page: Number(search?.page ?? 1),
      // order: search?.order,
      // sort_by: search.sort_by,
    } as GetCampaignHistoryOptions;
  },
  loader: ({ context: { queryClient }, location }) => {
    void Promise.all([
      queryClient.prefetchQuery(
        getCampaignHistoryQueryOptions(location.search),
      ),
      queryClient.prefetchQuery(getCurrentUserQueryOptions()),
    ]);
  },
  head: () => ({ meta: [{ title: "Campaigns" }] }),
  pendingComponent: () => (
    <div className="p-6 flex items-center justify-center">
      <Spinner delay={1000} />
    </div>
  ),
});

function CampaignTables() {
  const navigate = useNavigate();
  const routeParams = Route.useSearch();
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const queryParams = { ...routeParams, page: routeParams.page || 1 };
  const { data, isPending, isFetching } = useQuery(
    getCampaignHistoryQueryOptions(queryParams),
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: data?.results ?? [],
    columns,
    meta: { user },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {},
  });

  if (isPending) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Spinner delay={1000} />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex gap-2 p-4">
        <Select
          defaultValue="all"
          value={queryParams.state}
          onValueChange={(v) => {
            navigate({
              to: "/app/campaigns/history",
              search: {
                ...queryParams,
                page: 1,
                state: v === "all" ? undefined : (v as CampaignState),
              },
            }).catch(console.error);
          }}
        >
          <SimpleTooltip message="Filter campaigns by state">
            <SelectTrigger
              aria-label="Campaign State"
              className="w-[120px] shrink-0"
            >
              <SelectValue placeholder="State" />
            </SelectTrigger>
          </SimpleTooltip>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {campaignStateOptions.map((state) => (
              <SelectItem key={state} value={state}>
                {capitalCase(state)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div
        className={cn(
          "md:hidden",
          "border-t border-b border-slate-6 dark:border-slatedark-6",
          isFetching && "opacity-50",
        )}
      >
        {data && data.results.length > 0 ? (
          table.getRowModel().rows.map((row) => {
            const isCreator = user
              ? user.id === row.original.campaign.user_id
              : false;

            return (
              <Link
                to={
                  isCreator
                    ? "/app/campaigns/$campaignId/view"
                    : "/app/campaigns/$campaignId/review/$algoId"
                }
                params={{
                  campaignId: row.original.campaign.id.toString(),
                  algoId: row.original.algorithm.id.toString(),
                }}
                key={row.original.id}
                data-state={row.getIsSelected() && "selected"}
                className="group not-last:border-b border-slate-6 dark:border-slatedark-6 p-4 flex flex-col gap-4"
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <CampaignStateBadge campaign={row.original} />
                    <CampaignMoney campaign={row.original} user={user} />
                  </div>
                  <div className="flex gap-3 items-center">
                    <FeedAvatar
                      feed={row.original.algorithm}
                      className="w-12 h-12"
                    />
                    <div className="flex flex-col gap-0.5">
                      <div className="text-lg font-semibold">
                        {row.original.campaign.display_name}
                      </div>
                      <div className="truncate w-full text-slate-dim text-sm">
                        on {row.original.algorithm.display_name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="text-slate-dim">Creator</div>
                    <div>@{row.original.campaign_user.handle}</div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Progress
                    value={row.original.impressions_to_date}
                    max={
                      row.original.impressions_budget +
                      row.original.impressions_to_date
                    }
                  />
                  <div className="flex items-center gap-2 text-sm">
                    <div className="text-slate-dim">Impressions</div>
                    <div>
                      {row.original.impressions_to_date}
                      {" / "}
                      {row.original.impressions_budget +
                        row.original.impressions_to_date}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })
        ) : (
          <div className="p-6 flex items-center justify-center">
            You have no campaign history.
          </div>
        )}
      </div>
      <div
        className={cn(
          "hidden md:block",
          "border-t border-b border-slate-6 dark:border-slatedark-6",
          isFetching && "opacity-50",
        )}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="whitespace-pre"
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          {data && data.results.length > 0 ? (
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.original.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="group"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="whitespace-pre">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell>
                  <div className="text-slate-dim">
                    You have no campaign history.
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </div>
      {data && (
        <Pagination className="my-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPreviousLink
                to="/app/campaigns/history"
                search={(p) => ({ ...p, page: queryParams.page - 1 })}
                disabled={queryParams.page === 1}
              />
            </PaginationItem>
            {Array.from({ length: data.total_pages }).map((_, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <PaginationItem key={`page-${index}`}>
                <PaginationLink
                  to="/app/campaigns/history"
                  search={(p) => ({ ...p, page: index + 1 })}
                  isActive={queryParams.page === index + 1}
                >
                  {index + 1}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNextLink
                to="/app/campaigns/history"
                search={(p) => ({ ...p, page: queryParams.page + 1 })}
                disabled={queryParams.page === data.total_pages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
